<template>
    <div>
        <div ref="searchSelect" class="search-field" style="position: relative; margin-bottom: 16px">
            <div v-if="showResults && isDropdownOpen" class="search-results">
                {{ loading ? 'Loading...' : '' }}
                {{ !loading && searchResults.length === 0 ? 'No results found' : '' }}
                <ul v-if="isMultiSelect && !loading && markedSearchResults.length > 0" class="search-results__list">
                    <li
                        v-for="result of markedSearchResults"
                        :id="result.id"
                        :key="result.id"
                        class="search-results__check_item"
                        @click="handleTagAdded(result)"
                    >
                        <styled-checkbox
                            :id="result.id"
                            :key="result.id"
                            v-model="result.isSelected"
                            :class="['filter-checkbox', { checked: tags.includes(result.isSelected) }]"
                            :label="result.title"
                        />
                    </li>
                </ul>
                <ul v-else-if="!isMultiSelect && !loading && searchResults.length > 0" class="search-results__list">
                    <li
                        v-for="result of searchResults"
                        :key="result.id"
                        class="search-results__item"
                        @click="handleTagAdded(result)"
                    >
                        {{ result.title }}
                    </li>
                </ul>
            </div>
            <text-field
                :id="id"
                v-model="searchText"
                value
                :icon-before="searchIcon || (showSearchIcon ? SearchLightIcon : null)"
                :placeholder="placeholder"
                :label="label"
                :disabled="disabled"
                :error-messages="errorMessages"
                :required="required"
                :icon-close="showCloseIcon"
                @input="fetchResults"
                @blur="$emit('blur', searchText)"
                @clear="clearSearchText"
            />
        </div>
        <div v-if="!hideTags" class="selected-tags">
            <Tag v-for="tag of tags" :key="tag" :tag="tag" :remove-tag="removeTag" :disabled="disabled" />
        </div>
    </div>
</template>

<script>
import { debounce } from 'lodash-es';

import SearchLightIcon from '@/components/Icon/SearchLight.vue';
import Tag from '@/components/Input/SearchSelect/Tag.vue';
import StyledCheckbox from '@/components/StyledCheckbox.vue';
import TextField from '@/components/TextField.vue';

export default {
    name: 'SearchSelect',
    components: {
        TextField,
        Tag,
        StyledCheckbox,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        placeholder: {
            type: String,
            required: false,
            default: 'Search',
        },
        label: {
            type: String,
            required: false,
            default: '',
        },
        searchResults: {
            type: Array,
            required: false,
            default: () => {
                return [];
            },
        },
        loading: {
            type: Boolean,
            required: false,
        },
        tags: {
            type: Array,
            required: true,
        },
        addTag: {
            type: Function,
            required: true,
        },
        removeTag: {
            type: Function,
            required: true,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        errorMessages: {
            type: Array,
            required: false,
            default: () => {
                return [];
            },
        },
        setSelectedValueAsQuery: {
            type: Boolean,
            required: false,
            default: false,
        },
        showSearchIcon: {
            type: Boolean,
            required: false,
            default: true,
        },
        searchIcon: {
            type: Object,
            default: null,
        },
        defaultValue: {
            type: String,
            required: false,
            default: '',
        },
        required: {
            type: Boolean,
            required: false,
            default: false,
        },
        hideTags: {
            type: Boolean,
            required: false,
            default: false,
        },
        defaultText: {
            type: String,
            required: false,
            default: null,
        },
        showResults: {
            type: Boolean,
            required: false,
            default: true,
        },
        debounceDelay: {
            type: Number,
            required: false,
            default: 500,
        },
        minCharLimitForFetch: {
            type: Number,
            required: false,
            default: 3,
        },
        isMultiSelect: {
            type: Boolean,
            required: false,
            default: false,
        },
        allowDuplicate: {
            type: Boolean,
            required: false,
            default: true,
        },
        showCloseIcon: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            searchText: this.defaultText || '',
            isDropdownOpen: false,
            SearchLightIcon,
        };
    },
    computed: {
        markedSearchResults() {
            return this.isMultiSelect ?
                    this.searchResults.map(({ title }, index) => {
                        return {
                            id: index,
                            title,
                            isSelected: this.tags.includes(title),
                        };
                    })
                :   [];
        },
    },
    watch: {
        defaultValue(updatedDefaultValue) {
            this.searchText = updatedDefaultValue;
        },
    },
    created() {
        if (this.defaultValue) {
            this.searchText = this.defaultValue;
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
        fetchResults: debounce(function fetchResults(query) {
            if (query.length >= this.minCharLimitForFetch) {
                this.isDropdownOpen = true;
            } else {
                this.isDropdownOpen = false;
                return;
            }
            this.$emit('fetch-results', query);
        }, 500),
        handleClickOutside(event) {
            if (this.isDropdownOpen && !this.$refs.searchSelect.contains(event.target)) {
                this.isDropdownOpen = false;
            }
        },
        clearSearchText() {
            this.isDropdownOpen = false;
            this.searchText = '';
        },
        handleTagAdded(result) {
            if (this.isMultiSelect) {
                if (this.tags.includes(result.title)) {
                    this.removeTag(result.title);
                    return;
                }
            }

            if (!this.allowDuplicate && this.tags.includes(result.title)) {
                this.isDropdownOpen = false;
                this.searchText = '';
                return;
            }

            this.addTag(result);

            if (!this.isMultiSelect) {
                this.isDropdownOpen = false;
                this.searchText = '';
            }

            if (this.setSelectedValueAsQuery || this.hideTags) {
                this.searchText = result.title;
            }
        },
    },
};
</script>

<style scoped>
.text-field input {
    width: 324px !important;
}

.selected-tags {
    display: flex;
    flex-wrap: wrap;
}

.selected-tags .tag {
    margin-right: 6px;
    margin-bottom: 6px;
}

.search-results {
    position: absolute;
    background-color: #fff;
    padding: 8px 0px;
    width: 100%;
    max-height: 376px;
    height: auto !important;
    overflow-y: auto;
    bottom: 0px;
    transform: translateY(100%);
    border: 1px solid #dcdedf;
    border-radius: 4px;
    box-shadow: 0px 4px 4px 0px #00000026;
    z-index: 1;
}

.search-results .search-results__list {
    width: 100%;
    list-style-type: none;
    margin: 0;
    padding: 0;
}

.search-results .search-results__item {
    padding: 15px 16px;
}

.search-results .search-results__item:hover {
    background-color: #edf8fe;
    cursor: pointer;
}
.filter-checkbox {
    height: 44px !important;
}
</style>
